import classNames from 'classnames';
import { useState } from 'react';
import { AppHeader, Toolbar } from 'palette/AppHeader';
import { Modal, ModalBox, useModal } from 'palette/Modal';
import { STRATEGIES } from 'palette/Modal/constants/keyboardTrap';
import { SubHeaderDropdown, SubHeaderDropdownLink, SubHeaderDropdownSection } from 'palette/SubHeaderDropdown';
import { themeColor } from '@common/constants';
import { useConfig } from '@common/Hooks/useConfig';
import { useTranslation } from '@common/Hooks/useTranslation';
import { ReactOnRailsRootComponent } from '@common/ReactOnRails';
import { setNavFromValue } from '@common/Tracking/NavFrom';
import trackerAttributes from '@common/Tracking/NavFrom/constants/TrackedDataAttr';
import { menuSelectItemListTracking } from '@common/Tracking/Navigation';
import { BEMBlock } from '@helpers/classnames';
import { parseConfigArray } from '@helpers/config';
import { parseJSON } from '@helpers/data';
import { isFeatureActive } from '@helpers/index';
import { hasSecondaryLevelMenuItemDropdown, isNavConfigEnabled } from '@helpers/navigation';
import { getTranslatedPropertyFromContext } from '@helpers/translationContext';
import { HeaderBanner } from '../components/HeaderBanner';
import { useSpaceForBannerAvailable } from '../hooks/useSpaceForBannerAvailable';
import { MainHeader } from './MainHeader';
import {
    HEADER_DROPDOWN_IDS,
    DROPDOWN_MENU_MODAL_ID,
    DROPDOWN_CONTAINER_IDENTIFIER,
    DROPDOWN_SECTION_IDENTIFIER
} from './PageHeaderConstants';
import SubHeader from './SubHeader';
import '../styles/PageHeader.scss';

export function PageHeader({
    topMenuTree,
    customerDashboardUrl,
    checkoutShopcartUrl,
    imagesSourceUrl,
    uuids,
    customerServicePageUuids,
    tabIds,
    unisexTabIds,
    genderMenu: currentNavigation,
    dropdownFooterUSPS,
    languages,
    preloadedNavigation = null,
    breadcrumbs
}) {
    const { activeModalId, setActiveModalId } = useModal();
    const [isModalBoxActive, setIsModalBoxActive] = useState(false);
    const [hoveredSubHeaderNavItem, setHoveredSubHeaderNavItem] = useState();
    const translate = useTranslation();
    const config = useConfig();
    const isBooztletEnv = config?.webshop?.id === 'booztlet';
    const theme = !isBooztletEnv ? themeColor.light : themeColor.gray;
    const isHeaderNavModalActive = activeModalId === DROPDOWN_MENU_MODAL_ID;
    const isNewDesktopNav = isFeatureActive('PLT-33711-desktop-nav-refresh-2023', config);
    const [activeDropdownId, setActiveDropdownId] = useState();
    const [hoveredMainHeaderNavId, setHoveredMainHeaderNavId] = useState();
    const translatedUSPS = parseConfigArray(dropdownFooterUSPS).map(usp => ({ ...usp, label: translate(usp.label) }));
    const hasMainHeaderDropdown = currentNavigation && !isNavConfigEnabled(currentNavigation, 'desktopDropdownHide');
    const subHeaderNavigationTree = hasMainHeaderDropdown
        ? currentNavigation.children.find(({ uuid }) => uuid === parseJSON(uuids)[1])
        : currentNavigation;
    const [block, BEMElement] = BEMBlock('page-header');
    const subHeaderToolbarClasses = BEMElement('sub', { hidden: !subHeaderNavigationTree });
    const { top_cat: trackTopCat } = trackerAttributes;
    const isSubheaderDropdownOpen = isHeaderNavModalActive && activeDropdownId === HEADER_DROPDOWN_IDS.SUB_HEADER;
    const [selectedDepartment, setSelectedDepartment] = useState({
        id: currentNavigation?.navigationId,
        name: currentNavigation?.languageContext.en?.name || currentNavigation?.name
    });
    const currentDepartment = currentNavigation?.languageContext?.en?.name?.toLowerCase();
    const isEnoughSpaceForBanner = useSpaceForBannerAvailable();

    const { desktopBannerImage, desktopBannerLink, desktopBannerText } = hoveredSubHeaderNavItem || {};

    const isDesktopBannerFeatureEnabled = config.isActiveFeature('PLT-43108-banners-in-desktop-dropdown');
    const hasDesktopBanner = isEnoughSpaceForBanner
        && isDesktopBannerFeatureEnabled
        && desktopBannerImage;

    const isSplitColumns = (navGrandChildren) => {
        const splitLimit = isNewDesktopNav ? 13 : 7;
        return navGrandChildren.children.length > splitLimit;
    };

    const handleSetNavFromValue = (e) => {
        setNavFromValue(trackTopCat, e);
    };

    const handleDropdownModalClose = () => {
        setIsModalBoxActive(false);
    };

    const handleMainHeaderNavBarHover = (navItem) => {
        const showDropdownDesktop = !isNavConfigEnabled(navItem, 'desktopNavigationDropdownHide') && navItem.hasChildren;

        if (showDropdownDesktop) { setIsModalBoxActive(true); }
        setActiveModalId(showDropdownDesktop ? DROPDOWN_MENU_MODAL_ID : null);
        setHoveredMainHeaderNavId(showDropdownDesktop ? navItem.navigationId : null);
        setActiveDropdownId(showDropdownDesktop ? HEADER_DROPDOWN_IDS.MAIN_HEADER : null);
    };

    const handleSubHeaderNavBarHover = (navItem) => {
        const hasNecessaryContent = hasSecondaryLevelMenuItemDropdown(navItem, hasMainHeaderDropdown);

        setHoveredSubHeaderNavItem(navItem);
        setActiveModalId(hasNecessaryContent ? DROPDOWN_MENU_MODAL_ID : null);
        setActiveDropdownId(hasNecessaryContent ? HEADER_DROPDOWN_IDS.SUB_HEADER : null);
        if (hasNecessaryContent) { setIsModalBoxActive(true); }
    };

    const handleSubHeaderDropdownLinkClick = (event, navItem) => {
        handleSetNavFromValue(event);
        const topLevelMenuItem = topMenuTree.find(menuItem => (
            menuItem.navigationId === selectedDepartment.id
        ));
        // eslint-disable-next-line max-len
        const navItemLvl = !isNavConfigEnabled(topLevelMenuItem, 'desktopNavigationDropdownHide')
            ? 4
            : 3;
        const trackingNavItem = { lvl: navItemLvl, navigationId: navItem.navigationId, type: navItem.type };
        menuSelectItemListTracking(trackingNavItem, selectedDepartment);
    };

    return (
        <>
            <ModalBox
                ariaLabel={translate('main.header.navigation')}
                focusTrapStrategy={STRATEGIES.walkthrough}
                isActive={isModalBoxActive}
                isFocusTrapActive={activeDropdownId === HEADER_DROPDOWN_IDS.MAIN_HEADER && isModalBoxActive}
            >
                <AppHeader className={block}>
                    <div className={BEMElement('container')}>
                        <Toolbar
                            className={BEMElement('main')}
                            isNewDesktopNav={isNewDesktopNav}
                            theme={theme}
                        >
                            <MainHeader
                                activeDropdownId={activeDropdownId}
                                activeModalId={activeModalId}
                                breadcrumbs={breadcrumbs}
                                checkoutShopcartUrl={checkoutShopcartUrl}
                                currentDepartment={currentDepartment}
                                customerDashboardUrl={customerDashboardUrl}
                                customerServicePageUuids={customerServicePageUuids}
                                hoveredMainHeaderNavId={hoveredMainHeaderNavId}
                                imagesSourceUrl={imagesSourceUrl}
                                languages={languages}
                                preloadedNavigation={preloadedNavigation}
                                selectedDepartment={selectedDepartment}
                                setSelectedDepartment={setSelectedDepartment}
                                tabIds={tabIds}
                                topMenuTree={topMenuTree}
                                translatedUSPS={translatedUSPS}
                                unisexTabIds={unisexTabIds}
                                uuids={uuids}
                                v2={isNewDesktopNav}
                                onMainHeaderNavBarHover={handleMainHeaderNavBarHover}
                            />
                        </Toolbar>
                    </div>
                    {subHeaderNavigationTree && (
                        <div className={BEMElement('container')}>
                            <ModalBox
                                ariaLabel={isSubheaderDropdownOpen ? currentNavigation.name : null}
                                focusTrapStrategy={STRATEGIES.walkthrough}
                                isActive={isSubheaderDropdownOpen}
                            >
                                <Toolbar
                                    className={subHeaderToolbarClasses}
                                    isNewDesktopNav={isNewDesktopNav}
                                >
                                    <SubHeader
                                        activeDropdownId={activeDropdownId}
                                        activeDropdownItem={isSubheaderDropdownOpen ? hoveredSubHeaderNavItem : null}
                                        footerData={translatedUSPS}
                                        hasMainHeaderDropdown={hasMainHeaderDropdown}
                                        isDropdownMenuOpen={isHeaderNavModalActive}
                                        navTree={subHeaderNavigationTree}
                                        selectedDepartment={selectedDepartment}
                                        topMenuTree={topMenuTree}
                                        uuids={uuids}
                                        onSubHeaderNavBarHover={handleSubHeaderNavBarHover}
                                    />
                                </Toolbar>
                                <SubHeaderDropdown
                                    ariaLabel={`${currentNavigation.name} ${hoveredSubHeaderNavItem?.name}`}
                                    className={DROPDOWN_CONTAINER_IDENTIFIER}
                                    footerData={translatedUSPS}
                                    hasDesktopBanner={hasDesktopBanner}
                                    hoveredSubHeaderNavItem={hoveredSubHeaderNavItem}
                                    isOpen={isSubheaderDropdownOpen}
                                    v2={isNewDesktopNav}
                                >
                                    <div className={BEMElement('sub-header-dropdown')}>
                                        {/* Mounting all children in DOM for SEO purposes */}
                                        {subHeaderNavigationTree.children.map(({ children: navChildren = [], uuid }) => {
                                            const sectionClasses = classNames(BEMElement('sub-header-dropdown-section', {
                                                hidden: hoveredSubHeaderNavItem?.uuid !== uuid
                                            }), DROPDOWN_SECTION_IDENTIFIER);

                                            return hasMainHeaderDropdown ? (
                                                <SubHeaderDropdownSection
                                                    key={uuid}
                                                    className={sectionClasses}
                                                    splitColumns={navChildren.length > 7}
                                                    v2={isNewDesktopNav}
                                                >
                                                    {navChildren.map(navGrandChildren => (
                                                        <SubHeaderDropdownLink
                                                            key={navGrandChildren.uuid}
                                                            backgroundColor={navGrandChildren.highlight?.backgroundColor}
                                                            data-cs-id={navGrandChildren.idNameSlug}
                                                            hidden={hoveredSubHeaderNavItem?.uuid !== uuid}
                                                            href={`${config.URLPrefix}${getTranslatedPropertyFromContext(
                                                                navGrandChildren, 'url', config
                                                            )}`}
                                                            isActive={uuids.indexOf(navGrandChildren.uuid) > -1}
                                                            name={getTranslatedPropertyFromContext(
                                                                navGrandChildren, 'name', config
                                                            )}
                                                            text={navGrandChildren.highlight?.text}
                                                            textColor={navGrandChildren.highlight?.textColor}
                                                            onClick={event => handleSubHeaderDropdownLinkClick(
                                                                event,
                                                                navGrandChildren
                                                            )}
                                                            onContextMenu={handleSetNavFromValue}
                                                        />
                                                    ))}
                                                </SubHeaderDropdownSection>
                                            ) : navChildren.filter(navGrandChildren => navGrandChildren.children.length > 0)
                                                .map((navGrandChildren, index, arr) => (
                                                    <SubHeaderDropdownSection
                                                        key={navGrandChildren.uuid}
                                                        className={sectionClasses}
                                                        index={index}
                                                        isLastSection={index + 1 === arr.length}
                                                        name={getTranslatedPropertyFromContext(
                                                            navGrandChildren, 'name', config
                                                        )}
                                                        splitColumns={isSplitColumns(navGrandChildren)}
                                                        v2={isNewDesktopNav}
                                                    >
                                                        {navGrandChildren.children.map(navGrandGrandChildren => (
                                                            <SubHeaderDropdownLink
                                                                key={navGrandGrandChildren.uuid}
                                                                backgroundColor={navGrandGrandChildren.highlight?.backgroundColor}
                                                                data-cs-id={navGrandGrandChildren.idNameSlug}
                                                                hidden={hoveredSubHeaderNavItem?.uuid !== uuid}
                                                                href={`${config.URLPrefix}${getTranslatedPropertyFromContext(
                                                                    navGrandGrandChildren, 'url', config
                                                                )}`}
                                                                isActive={uuids.indexOf(navGrandGrandChildren.uuid) > -1}
                                                                name={getTranslatedPropertyFromContext(
                                                                    navGrandGrandChildren, 'name', config
                                                                )}
                                                                text={navGrandGrandChildren.highlight?.text}
                                                                textColor={navGrandGrandChildren.highlight?.textColor}
                                                                onClick={event => handleSubHeaderDropdownLinkClick(
                                                                    event,
                                                                    navGrandChildren
                                                                )}
                                                                onContextMenu={handleSetNavFromValue}
                                                            />
                                                        ))}
                                                    </SubHeaderDropdownSection>
                                                ));
                                        })}
                                    </div>
                                    { hasDesktopBanner && (
                                        <HeaderBanner
                                            imageSrc={desktopBannerImage}
                                            link={desktopBannerLink}
                                            text={desktopBannerText}
                                        />
                                    ) }
                                </SubHeaderDropdown>
                            </ModalBox>
                        </div>
                    )}
                </AppHeader>
            </ModalBox>
            <Modal
                closeOnOutsideHover
                disablePortal
                preventAutoFocus
                id={DROPDOWN_MENU_MODAL_ID}
                onClose={handleDropdownModalClose}
            />
        </>
    );
}

export const PageHeaderStandalone = ReactOnRailsRootComponent(PageHeader, { withReduxStore: true });
